/**
 * Merchant Pro V1 PR-2 — Reservation Detail page
 * (`/owner/reservations/{id}`).
 *
 * Deep-link Detail page backed by `GET /api/owner/reservations/{id}`
 * (new single-resource endpoint, V1-A1).
 *
 * Three panels:
 *   1. Customer info  — name, phone, tier badge
 *   2. Reservation details — date/time, party, branch, booking code
 *   3. Status timeline — created → confirmed → checked-in
 *
 * Three action buttons (operator-locked workflow):
 *   • Confirm  → POST /api/owner/reservations/{id}/approve
 *   • Check-in → POST /api/cashier/reservations/{id}/check-in
 *                (brand_owner admitted as part of PR-2 backend
 *                 allow-list extension)
 *   • Cancel   → POST /api/owner/reservations/{id}/reject
 *
 * OPERATOR-LOCKED ABSENCES (A=Hide):
 *   • NO "Mark No-Show" button
 *   • NO "Mark Completed" button
 *   • NO Notes editor / Notes panel
 *
 * REUSE discipline:
 *   • Reuses <PageHeader>, <SectionCard>, <StatusPill>, <FullSpinner>,
 *     <QueryError> primitives.
 *   • Reuses the activity-row pattern from OverviewPage:122-170 for
 *     the status timeline.
 *   • Reuses the existing 3 owner/cashier endpoints — zero new
 *     write APIs.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, CheckCircle2, LogIn, XCircle, User, Calendar,
  Hash, Building2, Clock, ShieldCheck, MoveRight, Crown, Timer,
  ArrowRight, Inbox, Play, Pause, RotateCcw, Square, Gamepad2,
  DollarSign, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import RequirePermission from '@/lib/capabilities';
import useSessionTicker from '@/hooks/useSessionTicker';

/**
 * Generate a cryptographically-random hex idempotency key in the 8…128
 * char range required by the server-side Pydantic contract. Centralised
 * here so all mutations on this page share the same generator.
 */
function _genIdempotencyKey() {
  const arr = new Uint8Array(12);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < 12; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

import {
  PageHeader,
  SectionCard,
  StatusPill,
  FullSpinner,
  QueryError,
  Spinner,
} from '@/components/ui_primitives/Misc';
import { classes } from '@/lib/utils';
import ChairGridTableCard from '@/components/owner/ChairGridTableCard';

/* StatusPill variant mapping — reuse the existing variants. */
const STATUS_PILL_VARIANT = {
  pending: 'amber',
  confirmed: 'gold',
  checked_in: 'blue',
  completed: 'green',
  cancelled_by_owner: 'red',
  cancelled_by_customer: 'red',
  cancelled_by_cashier: 'red',
  no_show: 'red',
};

function reservationStatusPill(status, testID) {
  const variant = STATUS_PILL_VARIANT[status] || 'slate';
  const label = (status || '').replace(/_/g, ' ');
  return (
    <StatusPill variant={variant} testID={testID}>
      {label}
    </StatusPill>
  );
}

function tierBadgeVariant(tier) {
  if (!tier) return 'slate';
  const t = String(tier).toLowerCase();
  if (t.includes('plat')) return 'gold';
  if (t.includes('gold')) return 'gold';
  if (t.includes('silver')) return 'slate';
  return 'slate';
}

function formatFull(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch {
    return iso;
  }
}

function DetailRow({ icon, label, value, testID }) {
  return (
    <div className="flex items-start gap-3 py-2.5" data-testid={testID}>
      <div className="mt-0.5 w-7 h-7 rounded-lg bg-gold/10 border border-gold/20 grid place-items-center text-gold shrink-0">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-white/45">
          {label}
        </div>
        <div className="text-sm text-white/90 mt-0.5 break-words">
          {value || '—'}
        </div>
      </div>
    </div>
  );
}

/* PR-4c — Compute a "T - HH:MM" countdown string. Past slots show
 * "+ HH:MM" (overdue) or "now" if within ±5 min. */
function computeCountdown(iso) {
  if (!iso) return { label: '—', tone: 'slate' };
  const target = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = target - now;
  const absMin = Math.abs(diffMs) / 60_000;
  if (absMin < 5) return { label: 'NOW', tone: 'gold' };
  const hours = Math.floor(absMin / 60);
  const minutes = Math.floor(absMin % 60);
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  if (diffMs >= 0) {
    return {
      label: `T - ${hh}:${mm}`,
      tone: absMin < 60 ? 'amber' : 'gold',
    };
  }
  return { label: `+ ${hh}:${mm}`, tone: 'red' };
}

/* PR-4c — Premium hero summary card. Hosts:
 *   • booking code monogram (large)
 *   • status pill + party-size pill
 *   • slot countdown timer
 *   • VIP tier ribbon (when membership.tier present)
 *   • <ChairGridTableCard size="sm"> thumbnail of the table assigned
 *     (folds in OQ-PR4c-2 dimensions: 120 × 100 · 32-px chairs)
 *
 * Source-of-truth comment: 4c-AC#3.
 */
function ReservationHeroCard({ reservation, tableThumb }) {
  const r = reservation || {};
  const countdown = computeCountdown(r.slot_start);
  const tier = r.membership?.tier;
  const tone = countdown.tone;
  const toneRing = {
    gold:  'border-gold/40 bg-gold/10 text-gold',
    amber: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
    red:   'border-red-500/40 bg-red-500/10 text-red-300',
    slate: 'border-white/15 bg-white/5 text-white/70',
  }[tone] || 'border-white/15 bg-white/5 text-white/70';
  return (
    <div
      data-testid="detail-hero-card"
      className="relative rounded-3xl overflow-hidden p-6 sm:p-7 mb-6"
      style={{
        background:
          'linear-gradient(135deg, rgba(212,175,55,0.10) 0%, rgba(21,21,27,0.95) 65%, rgba(21,21,27,1) 100%)',
        border: '1px solid rgba(212,175,55,0.25)',
        boxShadow: '0 18px 40px rgba(0,0,0,0.45)',
      }}
    >
      {tier ? (
        <span
          data-testid="detail-hero-vip-ribbon"
          className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-gold/90 to-amber-400 text-black text-[10px] uppercase tracking-widest font-bold shadow-lg"
        >
          <Crown size={11} />
          {tier}
        </span>
      ) : null}
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-5">
        {/* Booking code monogram */}
        <div className="flex-shrink-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-white/45 mb-1">
            Booking code
          </div>
          <div
            data-testid="detail-hero-booking-code"
            className="font-mono text-3xl sm:text-4xl text-gold tracking-wider font-bold"
            style={{ textShadow: '0 0 24px rgba(212,175,55,0.35)' }}
          >
            {r.booking_code || '—'}
          </div>
        </div>

        {/* Countdown + party */}
        <div className="flex-1 flex flex-col sm:flex-row gap-3 lg:ml-6">
          <div
            data-testid="detail-hero-countdown"
            className={classes(
              'inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border font-mono text-lg font-bold tracking-wider',
              toneRing,
            )}
          >
            <Timer size={16} />
            {countdown.label}
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-white/15 bg-white/5 text-white/85">
            <User size={14} />
            <span className="text-sm font-semibold">Party of {r.party_size ?? '—'}</span>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-white/15 bg-white/5 text-white/85">
            <Building2 size={14} />
            <span className="text-sm">{r.branch_name || r.branch_id?.slice(0, 8) || '—'}</span>
          </div>
        </div>

        {/* Table thumbnail (chair-grid size=sm — OQ-PR4c-2) */}
        {tableThumb ? (
          <div
            data-testid="detail-hero-table-thumb-wrap"
            className="flex-shrink-0 w-[120px]"
          >
            <ChairGridTableCard
              table={tableThumb}
              size="sm"
              testIdPrefix="detail-hero-table"
              showStatePill={false}
            />
            <div className="text-[10px] text-white/55 mt-1 text-center font-mono">
              {tableThumb.room_name || ''}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TimelineEntry({ when, title, who }) {
  return (
    <li className="flex items-start gap-3 py-2.5">
      <div className="mt-1 w-2 h-2 rounded-full bg-gold/70 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-white/90">{title}</div>
        <div className="text-[11px] text-white/45 mt-0.5">
          {formatFull(when)}
          {who ? <span className="ml-2 text-white/60">· {who}</span> : null}
        </div>
      </div>
    </li>
  );
}

function ActionButton({ tone, icon, label, onClick, disabled, testID }) {
  const ringClasses = {
    gold:  'border-gold/40 bg-gold/10 text-gold hover:bg-gold/20',
    blue:  'border-blue-500/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20',
    red:   'border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20',
    amber: 'border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20',
  }[tone] || 'border-white/15 bg-white/5 text-white/80 hover:bg-white/10';
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onClick}
      disabled={disabled}
      className={classes(
        'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        ringClasses,
      )}
    >
      {icon}
      {label}
    </button>
  );
}

export default function ReservationDetailPage() {
  const { reservationId } = useParams();
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Cancel-modal local state (Operator-locked workflow requires a
  // typed reason on Cancel).
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Move-table modal local state (PR-4a V4-A4).
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveTargetTableId, setMoveTargetTableId] = useState('');
  const [moveReason, setMoveReason] = useState('');

  // Phase V1.1-WP5 — Session End modal local state. Operator enters
  // optional revenue (Optional[int], ≥0, null allowed per OQ-V1.1-3).
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [endRevenue, setEndRevenue] = useState('');

  const detailQ = useQuery({
    queryKey: qk.ownerReservation(reservationId),
    queryFn: () => api.ownerReservationGet(token, reservationId),
    enabled: !!token && !!reservationId,
    staleTime: 15_000,
  });

  // PR-4c — pull floor-plan-state so the hero card can show the assigned
  // table as a chair-grid thumbnail. Disabled when there's no branch / no
  // assigned table — keeps the existing detail-only flow untouched.
  const branchIdForPlan = detailQ.data?.branch_id;
  const planQ = useQuery({
    queryKey: qk.ownerFloorPlanState(branchIdForPlan),
    queryFn: () => api.ownerFloorPlanState(token, branchIdForPlan),
    enabled: !!token && !!branchIdForPlan,
    staleTime: 30_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: qk.ownerReservation(reservationId) });
    queryClient.invalidateQueries({ queryKey: ['owner', 'reservations'] });
    queryClient.invalidateQueries({ queryKey: qk.ownerDashboardToday() });
  };

  const approveM = useMutation({
    mutationFn: () => api.ownerReservationApprove(token, reservationId),
    onSuccess: () => {
      toast.success('Reservation confirmed.');
      invalidate();
    },
    onError: (err) => toast.error(err?.detail || err?.message || 'Confirm failed.'),
  });

  const checkInM = useMutation({
    mutationFn: () => api.cashierReservationCheckIn(token, reservationId),
    onSuccess: () => {
      toast.success('Check-in recorded.');
      invalidate();
    },
    onError: (err) => toast.error(err?.detail || err?.message || 'Check-in failed.'),
  });

  const rejectM = useMutation({
    mutationFn: (reason) => api.ownerReservationReject(token, reservationId, reason),
    onSuccess: () => {
      toast.success('Reservation cancelled.');
      setShowCancelDialog(false);
      setCancelReason('');
      invalidate();
    },
    onError: (err) => toast.error(err?.detail || err?.message || 'Cancel failed.'),
  });

  // PR-4a V4-A4 + E1 — Move/Assign Table mutation.
  // Single mutation services both "Move Table" (legacy reassignment when
  // reservation.table_id is set) AND "Assign Table" (E1 first-time
  // assignment when reservation.table_id is NULL). Backend `move-table`
  // endpoint was widened to accept null source per E1.
  const moveTableM = useMutation({
    mutationFn: () => api.cashierReservationMoveTable(token, reservationId, {
      target_table_id: moveTargetTableId,
      reason: moveReason || undefined,
      idempotency_key: _genIdempotencyKey(),
    }),
    onSuccess: (_data, _vars, _ctx) => {
      // Use the at-call-time reservation.table_id captured in detailQ to
      // decide the assignment-vs-move messaging. After mutation the row
      // is invalidated and refetched.
      const wasAssign = !detailQ.data?.table_id;
      toast.success(wasAssign
        ? 'Table assigned to reservation.'
        : 'Reservation moved to new table.');
      setShowMoveDialog(false);
      setMoveTargetTableId('');
      setMoveReason('');
      invalidate();
    },
    onError: (err) => toast.error(
      err?.raw?.detail?.message || err?.detail || err?.message || 'Move failed.',
    ),
  });

  // Phase V1.1-WP5 — Session lifecycle mutations. All 4 share an
  // identical pattern: send a fresh idempotency_key, toast on success
  // (with the specific verb), invalidate, surface backend error code on
  // failure (e.g. 'session_concurrency_conflict', 'session_cashier_end_disabled').
  const _sessionErrorMsg = (err, fallback) => {
    // Backend uses HTTPException(detail={code, message}); request.js
    // captures the response body on `err.raw`.
    const detail = err?.raw?.detail || err?.detail;
    if (detail && typeof detail === 'object') {
      return detail.message || detail.code || fallback;
    }
    return err?.message || fallback;
  };

  const sessionStartM = useMutation({
    mutationFn: () => api.cashierSessionStart(token, reservationId, {
      idempotency_key: _genIdempotencyKey(),
    }),
    onSuccess: () => {
      toast.success('Session started.');
      invalidate();
    },
    onError: (err) => toast.error(_sessionErrorMsg(err, 'Session start failed.')),
  });

  const sessionPauseM = useMutation({
    mutationFn: () => api.cashierSessionPause(token, reservationId, {
      idempotency_key: _genIdempotencyKey(),
    }),
    onSuccess: () => {
      toast.success('Session paused.');
      invalidate();
    },
    onError: (err) => toast.error(_sessionErrorMsg(err, 'Session pause failed.')),
  });

  const sessionResumeM = useMutation({
    mutationFn: () => api.cashierSessionResume(token, reservationId, {
      idempotency_key: _genIdempotencyKey(),
    }),
    onSuccess: () => {
      toast.success('Session resumed.');
      invalidate();
    },
    onError: (err) => toast.error(_sessionErrorMsg(err, 'Session resume failed.')),
  });

  const sessionEndM = useMutation({
    mutationFn: () => {
      const trimmed = endRevenue.trim();
      const body = { idempotency_key: _genIdempotencyKey() };
      if (trimmed !== '') {
        const parsed = Number(trimmed);
        // 422 will be surfaced by backend on negative/non-integer
        body.revenue_rupiah = Number.isFinite(parsed) ? parsed : trimmed;
      } else {
        body.revenue_rupiah = null;
      }
      return api.cashierSessionEnd(token, reservationId, body);
    },
    onSuccess: () => {
      toast.success('Session ended.');
      setShowEndDialog(false);
      setEndRevenue('');
      invalidate();
    },
    onError: (err) => toast.error(_sessionErrorMsg(err, 'Session end failed.')),
  });

  if (detailQ.isLoading) return <FullSpinner />;
  if (detailQ.isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          kicker="Reservation"
          title="Reservation Detail"
          subtitle="Single-reservation read."
        />
        <QueryError error={detailQ.error} onRetry={detailQ.refetch} />
      </div>
    );
  }

  const r = detailQ.data || {};
  const status = r.status;
  const customer = r.customer || {};
  const membership = r.membership || {};

  // PR-4c — resolve table thumbnail data (hero card chair-grid).
  const planTables = planQ.data?.tables || [];
  const planRooms = planQ.data?.rooms || [];
  const heroTable = r.table_id
    ? (() => {
        const t = planTables.find((x) => x.id === r.table_id);
        if (!t) return null;
        const room = planRooms.find((rm) => rm.id === t.room_id);
        return { ...t, room_name: room?.name };
      })()
    : null;

  // Eligibility predicates per operator-locked workflow.
  const canConfirm = status === 'requested' || status === 'held_pending';
  const canCheckIn = status === 'confirmed';
  const isTerminal =
    status === 'cancelled_by_owner' ||
    status === 'cancelled_by_customer' ||
    status === 'cancelled_by_cashier' ||
    status === 'cancelled_by_system' ||
    status === 'no_show' ||
    status === 'completed' ||
    status === 'expired';
  const canCancel = !isTerminal && status !== 'checked_in';
  // PR-4a V4-A4 — Move enables on confirmed or checked_in only.
  const canMoveTable = status === 'confirmed' || status === 'checked_in';

  return (
    <div className="space-y-6" data-testid="owner-reservation-detail-page">
      <div className="flex items-center gap-2 text-xs text-white/55">
        <Link
          to="/owner/reservations"
          className="inline-flex items-center gap-1 text-gold/85 hover:text-gold"
          data-testid="back-to-list-link"
        >
          <ArrowLeft size={12} /> All reservations
        </Link>
      </div>

      <PageHeader
        kicker="Reservation"
        title={r.booking_code || 'Reservation Detail'}
        subtitle={`Status: ${(status || '').replace(/_/g, ' ')}`}
        actions={
          <div className="flex items-center gap-2">
            {reservationStatusPill(status, 'detail-header-status')}
          </div>
        }
      />

      {/* PR-4c — Premium hero summary card (4c-AC#3). */}
      <ReservationHeroCard reservation={r} tableThumb={heroTable} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Customer info + Reservation details */}
        <div className="lg:col-span-2 space-y-6">
          <SectionCard testID="detail-customer-card">
            <div className="flex items-center gap-2 mb-3">
              <User size={14} className="text-gold" />
              <h2 className="font-display text-lg font-semibold text-white">
                Customer
              </h2>
              {membership?.tier ? (
                <StatusPill
                  variant={tierBadgeVariant(membership.tier)}
                  testID="detail-tier-badge"
                >
                  {membership.tier}
                </StatusPill>
              ) : null}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <DetailRow
                icon={<User size={14} />}
                label="Name"
                value={customer.name || '—'}
                testID="detail-customer-name"
              />
              <DetailRow
                icon={<Hash size={14} />}
                label="Phone"
                value={customer.phone || '—'}
                testID="detail-customer-phone"
              />
              <DetailRow
                icon={<ShieldCheck size={14} />}
                label="No-show count (90d)"
                value={String(r.no_show_count_90d ?? 0)}
                testID="detail-no-show-count"
              />
              <DetailRow
                icon={<ShieldCheck size={14} />}
                label="Membership"
                value={membership?.has_active ? 'Active' : 'Inactive'}
                testID="detail-membership-status"
              />
            </div>
          </SectionCard>

          <SectionCard testID="detail-reservation-card">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={14} className="text-gold" />
              <h2 className="font-display text-lg font-semibold text-white">
                Reservation Details
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <DetailRow
                icon={<Calendar size={14} />}
                label="Date & Time"
                value={formatFull(r.slot_start)}
                testID="detail-slot-start"
              />
              <DetailRow
                icon={<Clock size={14} />}
                label="Ends"
                value={formatFull(r.slot_end)}
                testID="detail-slot-end"
              />
              <DetailRow
                icon={<User size={14} />}
                label="Party Size"
                value={String(r.party_size ?? '—')}
                testID="detail-party-size"
              />
              <DetailRow
                icon={<Hash size={14} />}
                label="Booking Code"
                value={r.booking_code || '—'}
                testID="detail-booking-code"
              />
              <DetailRow
                icon={<Building2 size={14} />}
                label="Branch"
                value={r.branch_id || '—'}
                testID="detail-branch-id"
              />
              <DetailRow
                icon={<ShieldCheck size={14} />}
                label="Source"
                value={(r.source || '').replace(/_/g, ' ') || '—'}
                testID="detail-source"
              />
            </div>
          </SectionCard>
        </div>

        {/* RIGHT: Action buttons + Status timeline */}
        <div className="space-y-6">
          <SectionCard testID="detail-actions-card">
            <h2 className="font-display text-lg font-semibold text-white mb-3">
              Actions
            </h2>
            <div className="flex flex-col gap-2">
              {/* Sub-phase RBAC-C2 (2026-06-18) — 4 capability wraps.
                  GM lacks `reservation.confirm`, `reservation.check_in`,
                  `reservation.mark_no_show`, `reservation.mark_completed`
                  in the C1 seed → only Cancel + Move table render for GM.
                  Cashier + Brand Owner + Supervisor see all 4. */}
              <RequirePermission cap="reservation.confirm">
                <ActionButton
                  tone="gold"
                  icon={
                    approveM.isPending ? (
                      <Spinner className="w-4 h-4" />
                    ) : (
                      <CheckCircle2 size={14} />
                    )
                  }
                  label="Confirm"
                  onClick={() => approveM.mutate()}
                  disabled={!canConfirm || approveM.isPending}
                  testID="action-confirm"
                />
              </RequirePermission>
              <RequirePermission cap="reservation.check_in">
                <ActionButton
                  tone="blue"
                  icon={
                    checkInM.isPending ? (
                      <Spinner className="w-4 h-4" />
                    ) : (
                      <LogIn size={14} />
                    )
                  }
                  label="Check-in"
                  onClick={() => checkInM.mutate()}
                  disabled={!canCheckIn || checkInM.isPending}
                  testID="action-checkin"
                />
              </RequirePermission>
              <RequirePermission cap="reservation.cancel">
                <ActionButton
                  tone="red"
                  icon={<XCircle size={14} />}
                  label="Cancel"
                  onClick={() => setShowCancelDialog(true)}
                  disabled={!canCancel}
                  testID="action-cancel"
                />
              </RequirePermission>
              <RequirePermission cap="reservation.move_table">
                <ActionButton
                  tone="amber"
                  icon={<MoveRight size={14} />}
                  label={r.table_id ? 'Move table' : 'Assign table'}
                  onClick={() => setShowMoveDialog(true)}
                  disabled={!canMoveTable}
                  testID="action-move-table"
                />
              </RequirePermission>
            </div>
            <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
              Confirm enables when status is pending. Check-in enables when
              confirmed. Cancel disabled on completed / cancelled rows.
              {r.table_id
                ? ' Move enables on confirmed or checked-in.'
                : ' Assign Table enables on confirmed or checked-in reservations that have no table bound.'}
            </p>
          </SectionCard>

          {/* Phase V1.1-WP5 — Session Operations panel.
              Renders inline on existing ReservationDetailPage; NO new
              page, NO new module. Surfaces session state, live ticker
              (`useSessionTicker`), GM, optional game_type, and the 4
              capability-gated lifecycle actions. End opens a modal
              with optional `revenue_rupiah` (OQ-V1.1-3). */}
          <SessionOpsPanel
            reservation={r}
            sessionStartM={sessionStartM}
            sessionPauseM={sessionPauseM}
            sessionResumeM={sessionResumeM}
            onEndClick={() => setShowEndDialog(true)}
          />

          <SectionCard testID="detail-timeline-card">
            <h2 className="font-display text-lg font-semibold text-white mb-3">
              Status Timeline
            </h2>
            <ul className="divide-y divide-white/5">
              <TimelineEntry
                when={r.slot_start}
                title="Reservation created"
                who={r.source ? `via ${r.source.replace(/_/g, ' ')}` : null}
              />
              {r.confirmed_at ? (
                <TimelineEntry
                  when={r.confirmed_at}
                  title="Confirmed"
                  who={r.confirmed_by_role || null}
                />
              ) : null}
              {r.checked_in_at ? (
                <TimelineEntry
                  when={r.checked_in_at}
                  title="Checked in"
                  who={r.checked_in_by_user_id ? 'cashier/owner' : null}
                />
              ) : null}
              {r.cancellation_reason ? (
                <TimelineEntry
                  when={r.slot_start}
                  title="Cancelled"
                  who={r.cancellation_reason}
                />
              ) : null}
            </ul>
          </SectionCard>
        </div>
      </div>

      {/* Cancel reason modal */}
      {showCancelDialog ? (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4"
          data-testid="cancel-dialog"
        >
          <div className="bg-[#15151B] border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="font-display text-lg font-semibold text-white mb-1">
              Cancel reservation
            </h3>
            <p className="text-sm text-white/55 mb-4">
              Please provide a brief cancellation reason. The customer is
              notified automatically.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="e.g. Customer requested by phone"
              data-testid="cancel-reason-input"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancelReason('');
                }}
                disabled={rejectM.isPending}
                data-testid="cancel-dialog-back"
                className="px-3 py-1.5 text-sm text-white/70 hover:text-white"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => rejectM.mutate(cancelReason || 'No reason provided')}
                disabled={rejectM.isPending}
                data-testid="cancel-dialog-confirm"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-red-500/40 bg-red-500/15 text-red-200 hover:bg-red-500/25 disabled:opacity-50"
              >
                {rejectM.isPending ? <Spinner className="w-4 h-4" /> : <XCircle size={14} />}
                Cancel reservation
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Move-table modal (PR-4a V4-A4) */}
      {showMoveDialog ? (
        <MoveTableDialog
          token={token}
          reservation={r}
          targetTableId={moveTargetTableId}
          setTargetTableId={setMoveTargetTableId}
          reason={moveReason}
          setReason={setMoveReason}
          onClose={() => { setShowMoveDialog(false); setMoveTargetTableId(''); setMoveReason(''); }}
          onSubmit={() => moveTableM.mutate()}
          isPending={moveTableM.isPending}
        />
      ) : null}

      {/* Phase V1.1-WP5 — End Session modal (revenue input, OQ-V1.1-3 LOCKED).
          Optional integer ≥0; null allowed (operator did not record revenue).
          Negative or non-integer → backend 422; the page-level error toast
          surfaces the violation. */}
      {showEndDialog ? (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4"
          data-testid="end-session-dialog"
        >
          <div className="bg-[#15151B] border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="font-display text-lg font-semibold text-white mb-1">
              End session
            </h3>
            <p className="text-sm text-white/55 mb-4">
              Record session revenue (optional). Leave blank if revenue
              was not captured. The reservation will be marked completed
              and the table moved into cleaning.
            </p>
            <label
              htmlFor="end-session-revenue"
              className="block text-[11px] uppercase tracking-wide text-white/40 mb-1"
            >
              Revenue (Rupiah)
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/50">Rp</span>
              <input
                id="end-session-revenue"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                value={endRevenue}
                onChange={(e) => setEndRevenue(e.target.value)}
                placeholder="e.g. 250000"
                data-testid="end-session-revenue-input"
                className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
              />
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => { setShowEndDialog(false); setEndRevenue(''); }}
                disabled={sessionEndM.isPending}
                data-testid="end-session-dialog-back"
                className="px-3 py-1.5 text-sm text-white/70 hover:text-white"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => sessionEndM.mutate()}
                disabled={sessionEndM.isPending}
                data-testid="end-session-dialog-confirm"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gold/40 bg-gold/15 text-gold hover:bg-gold/25 disabled:opacity-50"
              >
                {sessionEndM.isPending ? <Spinner className="w-4 h-4" /> : <Square size={14} />}
                End session
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Phase V1.1-WP5 — Session Operations panel (inline component, ~120 LOC).
 *
 * Renders a read-only state row (state badge + live ticker + GM + game)
 * plus 4 capability-gated lifecycle buttons. Visibility rules:
 *
 *   • Start  ←→ session_state in {null, 'ended'} AND status == 'checked_in'
 *   • Pause  ←→ session_state == 'active'
 *   • Resume ←→ session_state == 'paused'
 *   • End    ←→ session_state in {'active', 'paused'}
 *
 * Cashier-end gating happens server-side (Brand.session_cashier_end_enabled);
 * the End button always renders for capability-holders — the 403 toast
 * surfaces the brand-toggle denial naturally.
 *
 * Reuses <SectionCard>, <ActionButton>, <RequirePermission>,
 * `useSessionTicker`. NO new design tokens, NO new primitives.
 */
function SessionOpsPanel({
  reservation,
  sessionStartM,
  sessionPauseM,
  sessionResumeM,
  onEndClick,
}) {
  const state = reservation?.session_state || null;
  const status = reservation?.status || null;
  const { label: tickerLabel } = useSessionTicker({
    startedAt: reservation?.session_started_at,
    pausedAt: reservation?.session_paused_at,
    pausedTotalMs: reservation?.session_paused_total_ms ?? 0,
    sessionState: state,
  });

  const canStart =
    (state === null || state === undefined || state === 'ended') &&
    status === 'checked_in';
  const canPause = state === 'active';
  const canResume = state === 'paused';
  const canEnd = state === 'active' || state === 'paused';

  // State badge styling — playing=purple, paused=amber, ended=neutral.
  const stateBadge = (() => {
    if (state === 'active') {
      return { label: 'ACTIVE', color: '#8B5CF6', bg: 'rgba(139,92,246,0.15)' };
    }
    if (state === 'paused') {
      return { label: 'PAUSED', color: '#FBBF24', bg: 'rgba(251,191,36,0.15)' };
    }
    if (state === 'ended') {
      return { label: 'ENDED', color: '#94A3B8', bg: 'rgba(148,163,184,0.15)' };
    }
    return { label: 'NOT STARTED', color: '#64748B', bg: 'rgba(100,116,139,0.10)' };
  })();

  const fmtRevenue = (rp) => {
    if (rp === null || rp === undefined) return null;
    try {
      return new Intl.NumberFormat('id-ID').format(rp);
    } catch {
      return String(rp);
    }
  };
  const revenueLabel = fmtRevenue(reservation?.session_revenue_rupiah);

  return (
    <SectionCard testID="detail-session-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold text-white">
          Session
        </h2>
        <span
          data-testid="session-state-badge"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: stateBadge.color, backgroundColor: stateBadge.bg }}
        >
          <Activity size={12} />
          {stateBadge.label}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        <DetailRow
          icon={<Timer size={14} />}
          label="Elapsed"
          value={
            <span data-testid="session-elapsed-ticker" className="font-mono">
              {tickerLabel}
            </span>
          }
        />
        {reservation?.session_gm_id ? (
          <DetailRow
            icon={<User size={14} />}
            label="Game Master"
            value={
              <span data-testid="session-gm-id" className="font-mono text-xs">
                {reservation.session_gm_id}
              </span>
            }
          />
        ) : null}
        {reservation?.session_game_type ? (
          <DetailRow
            icon={<Gamepad2 size={14} />}
            label="Game"
            value={
              <span data-testid="session-game-type">
                {reservation.session_game_type}
              </span>
            }
          />
        ) : null}
        {revenueLabel !== null ? (
          <DetailRow
            icon={<DollarSign size={14} />}
            label="Revenue"
            value={
              <span data-testid="session-revenue">{`Rp ${revenueLabel}`}</span>
            }
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <RequirePermission cap="session.start">
          <ActionButton
            tone="gold"
            icon={
              sessionStartM.isPending ? (
                <Spinner className="w-4 h-4" />
              ) : (
                <Play size={14} />
              )
            }
            label="Start Session"
            onClick={() => sessionStartM.mutate()}
            disabled={!canStart || sessionStartM.isPending}
            testID="action-session-start"
          />
        </RequirePermission>
        <RequirePermission cap="session.pause">
          <ActionButton
            tone="amber"
            icon={
              sessionPauseM.isPending ? (
                <Spinner className="w-4 h-4" />
              ) : (
                <Pause size={14} />
              )
            }
            label="Pause Session"
            onClick={() => sessionPauseM.mutate()}
            disabled={!canPause || sessionPauseM.isPending}
            testID="action-session-pause"
          />
        </RequirePermission>
        <RequirePermission cap="session.resume">
          <ActionButton
            tone="blue"
            icon={
              sessionResumeM.isPending ? (
                <Spinner className="w-4 h-4" />
              ) : (
                <RotateCcw size={14} />
              )
            }
            label="Resume Session"
            onClick={() => sessionResumeM.mutate()}
            disabled={!canResume || sessionResumeM.isPending}
            testID="action-session-resume"
          />
        </RequirePermission>
        <RequirePermission cap="session.end">
          <ActionButton
            tone="red"
            icon={<Square size={14} />}
            label="End Session"
            onClick={onEndClick}
            disabled={!canEnd}
            testID="action-session-end"
          />
        </RequirePermission>
      </div>
      <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
        Start enables on checked-in reservations. Pause/Resume gated by
        current session state. End opens a revenue modal and cascades the
        reservation to completed and the table to cleaning. Cashier End
        requires the brand-wide setting to be enabled.
      </p>
    </SectionCard>
  );
}

/**
 * Move-table modal — PR-4c visual upgrade · Phase E3 PR4d-5 sort control.
 *
 * Replaces the legacy `<select>` target dropdown with a chair-grid
 * target picker (`<ChairGridTableCard size="sm">` per option) and adds
 * an empty-state messaging panel + a source→target visual arrow on the
 * confirm row.
 *
 * Phase E3 PR4d-5 — Sort control sits directly above the chair-grid
 * target picker. Three options:
 *   • Room + label   — sort by room name then label (DEFAULT)
 *   • Capacity ↑     — sort by capacity ascending; tie-break by label
 *   • Distance       — sort by Euclidean distance from source table's
 *                       x_coord/y_coord (when both source and target
 *                       have coords); tie-break by capacity ascending.
 *
 * Default = "Room + label" (rationale: matches the PR-4c locked default
 * + operators are already familiar with it · no behavioural surprise).
 *
 * Distance availability: enabled on reassign-path (source table_id set),
 * disabled-with-tooltip on assign-path (source null, Phase E1 widening)
 * — affordance stays visible so operators can discover it.
 */
function MoveTableDialog({ token, reservation, targetTableId, setTargetTableId, reason, setReason, onClose, onSubmit, isPending }) {
  const branchId = reservation?.branch_id;
  const planQ = useQuery({
    queryKey: qk.ownerFloorPlanState(branchId),
    queryFn: () => api.ownerFloorPlanState(token, branchId),
    enabled: !!token && !!branchId,
    staleTime: 5_000,
  });
  const rooms = planQ.data?.rooms ?? [];
  const allTables = planQ.data?.tables ?? [];
  const partySize = Number(reservation?.party_size || 1);
  const roomNameById = Object.fromEntries(rooms.map((r) => [r.id, r.name]));

  // PR4d-5 — Sort state. Default 'room' per OQ-PR4c-1 + operator brief.
  const [sortMode, setSortMode] = useState('room');

  // E1 widening — when reservation.table_id is NULL, this dialog acts as
  // the first-time "Assign Table" surface (per E1 brief §2). Otherwise
  // the legacy "Move Table" semantics apply. Single dialog, conditional
  // copy only — no fork, no new component.
  const isAssign = !reservation?.table_id;
  const sourceTable = reservation?.table_id
    ? allTables.find((t) => t.id === reservation.table_id) || null
    : null;
  const sourceTableName = sourceTable
    ? (sourceTable.name || 'Current')
    : 'No table assigned';

  // Distance-sort availability — needs a source table with normalized
  // coords AND target tables with coords. We gate at the source level
  // here; per-target fallbacks happen inside the comparator.
  const hasSourceCoords =
    sourceTable != null &&
    typeof sourceTable.x_coord === 'number' &&
    typeof sourceTable.y_coord === 'number';
  const distanceDisabled = isAssign || !hasSourceCoords;
  const distanceDisabledReason = isAssign
    ? 'No source table — assign-path has nothing to measure from.'
    : (!hasSourceCoords
        ? 'Source table has no floor-plan coordinates. Set them via the Branch admin to enable Distance sort.'
        : '');

  // PR4d-5 — Build the candidate list once, then sort by the active mode.
  // `useMemo` is intentional: keeps the chair-grid stable across renders
  // that don't actually change the candidate set or the sort key, so
  // selection state on the cards doesn't flicker.
  const baseCandidates = allTables.filter((t) =>
    t.id !== reservation?.table_id &&
    (t.state ?? 'available') === 'available' &&
    Number(t.capacity || 0) >= partySize,
  );

  const availableTables = (() => {
    const list = [...baseCandidates];
    if (sortMode === 'capacity') {
      list.sort((a, b) => {
        const ac = Number(a.capacity || 0);
        const bc = Number(b.capacity || 0);
        if (ac !== bc) return ac - bc;
        const al = (a.name || a.label || '').toLowerCase();
        const bl = (b.name || b.label || '').toLowerCase();
        return al < bl ? -1 : al > bl ? 1 : 0;
      });
    } else if (sortMode === 'distance' && !distanceDisabled) {
      // Euclidean distance on normalized [0..1] coords. Tables without
      // coords sort to the end via `Infinity`; ties → capacity ascending.
      const sx = sourceTable.x_coord;
      const sy = sourceTable.y_coord;
      const distOf = (t) => {
        if (typeof t.x_coord !== 'number' || typeof t.y_coord !== 'number') {
          return Number.POSITIVE_INFINITY;
        }
        const dx = t.x_coord - sx;
        const dy = t.y_coord - sy;
        return Math.sqrt(dx * dx + dy * dy);
      };
      list.sort((a, b) => {
        const da = distOf(a);
        const db = distOf(b);
        if (da !== db) return da - db;
        return Number(a.capacity || 0) - Number(b.capacity || 0);
      });
    } else {
      // Default: Room + label ASC.
      list.sort((a, b) => {
        const ar = (roomNameById[a.room_id] || '').toLowerCase();
        const br = (roomNameById[b.room_id] || '').toLowerCase();
        if (ar !== br) return ar < br ? -1 : 1;
        const al = (a.name || a.label || '').toLowerCase();
        const bl = (b.name || b.label || '').toLowerCase();
        return al < bl ? -1 : al > bl ? 1 : 0;
      });
    }
    return list;
  })();

  const selectedTarget = availableTables.find((t) => t.id === targetTableId) || null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4 py-8"
      data-testid="move-table-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#15151B] border border-white/10 rounded-2xl p-6 max-w-3xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3
              className="font-display text-lg font-semibold text-white"
              data-testid="move-table-dialog-title"
            >
              {isAssign
                ? 'Assign reservation to a table'
                : 'Move reservation to another table'}
            </h3>
            <p className="text-sm text-white/55 mt-1">
              Choose a target table. Options below show only Available tables with capacity ≥ {partySize}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="move-table-dialog-close"
            className="text-white/55 hover:text-white text-xl leading-none px-2 -mt-1"
          >
            ×
          </button>
        </div>

        {/* Source → Target indicator row */}
        <div
          data-testid="move-table-source-target"
          className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-black/40 border border-white/10 mb-4"
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-white/45">
              From
            </div>
            <div
              className={classes(
                'text-sm font-medium mt-0.5 truncate',
                isAssign ? 'italic text-white/35' : 'text-amber-300',
              )}
              data-testid="move-table-source-summary"
            >
              {sourceTableName}
            </div>
          </div>
          <ArrowRight size={20} className="text-gold shrink-0" />
          <div className="min-w-0 text-right">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-white/45">
              To
            </div>
            <div className={classes(
              'text-sm font-medium mt-0.5 truncate',
              selectedTarget ? 'text-emerald-300' : 'text-white/35',
            )}
              data-testid="move-table-target-summary"
            >
              {selectedTarget
                ? `${roomNameById[selectedTarget.room_id] || 'Room'} · ${selectedTarget.name}`
                : 'Select a target…'}
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-white/45 ml-2">
            Party {partySize}
          </span>
        </div>

        {/* PR4d-5 — Sort control. 3-option segment above the chair grid. */}
        {availableTables.length > 0 || planQ.isLoading ? (
          <div
            data-testid="move-table-sort-control"
            className="flex items-center gap-2 mb-3"
          >
            <span className="text-[10px] uppercase tracking-widest font-semibold text-white/45 mr-1">
              Sort
            </span>
            <SortChip
              testId="move-table-sort-room"
              active={sortMode === 'room'}
              onClick={() => setSortMode('room')}
              label="Room + label"
            />
            <SortChip
              testId="move-table-sort-capacity"
              active={sortMode === 'capacity'}
              onClick={() => setSortMode('capacity')}
              label="Capacity ↑"
            />
            <SortChip
              testId="move-table-sort-distance"
              active={sortMode === 'distance' && !distanceDisabled}
              onClick={() => { if (!distanceDisabled) setSortMode('distance'); }}
              disabled={distanceDisabled}
              tooltip={distanceDisabledReason}
              label="Distance from source"
            />
          </div>
        ) : null}

        {/* Target picker — chair-grid */}
        {planQ.isLoading ? (
          <div className="text-center py-6"><Spinner className="w-5 h-5 inline-block" /></div>
        ) : availableTables.length === 0 ? (
          <div
            data-testid="move-table-no-targets"
            className="rounded-2xl bg-white/[0.02] border border-white/10 px-6 py-10 text-center"
          >
            <div className="inline-flex w-14 h-14 rounded-2xl border border-amber-500/30 bg-amber-500/10 grid place-items-center text-amber-300 mb-3">
              <Inbox size={26} />
            </div>
            <div className="text-sm font-semibold text-white">
              No eligible target tables
            </div>
            <p className="text-xs text-white/55 mt-1 max-w-md mx-auto leading-relaxed">
              {isAssign
                ? `All tables at this branch with capacity ≥ ${partySize} are currently occupied, blocked, or cleaning. Free up a table or wait for one to become available — then retry the assignment.`
                : `All other tables in this branch are either occupied, blocked, cleaning, or too small for a party of ${partySize}. Free up a table or wait for one to become available — then retry the move.`}
            </p>
          </div>
        ) : (
          <div
            data-testid="move-table-target-grid"
            data-sort-mode={sortMode}
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}
          >
            {availableTables.map((t) => (
              <div
                key={t.id}
                data-testid={`move-table-option-wrap-${t.id}`}
              >
                <ChairGridTableCard
                  table={t}
                  selected={t.id === targetTableId}
                  onClick={() => setTargetTableId(t.id)}
                  testIdPrefix="move-table-option"
                  size="sm"
                />
                <div className="text-[10px] text-white/45 mt-1 text-center truncate">
                  {roomNameById[t.room_id] || 'Room'}
                </div>
              </div>
            ))}
          </div>
        )}

        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="Optional reason (e.g. VIP request, equipment)"
          maxLength={500}
          data-testid="move-table-reason"
          className="mt-5 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
        />
        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="move-table-cancel"
            className="px-3 py-1.5 text-sm text-white/70 hover:text-white"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isPending || !targetTableId}
            data-testid="move-table-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-amber-500/40 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 disabled:opacity-50"
          >
            {isPending ? <Spinner className="w-4 h-4" /> : <MoveRight size={14} />}
            {isAssign ? 'Confirm assignment' : 'Confirm move'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Phase E3 PR4d-5 — small segmented-chip used by the Move Table dialog
 * sort control. Disabled chips show a tooltip via the native `title`
 * attribute and remain VISIBLE (per AC3) so the affordance stays
 * discoverable on the assign-path. Pure rendering helper — no API,
 * no router, no shared state outside the parent component.
 */
function SortChip({ testId, active, onClick, label, disabled = false, tooltip = '' }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? tooltip : undefined}
      data-testid={testId}
      data-active={active ? 'true' : 'false'}
      data-disabled={disabled ? 'true' : 'false'}
      className={classes(
        'inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide border transition-colors',
        active
          ? 'bg-gold/20 border-gold/50 text-gold'
          : 'bg-white/[0.03] border-white/10 text-white/65 hover:text-white hover:border-white/25',
        disabled ? 'opacity-40 cursor-not-allowed hover:text-white/65 hover:border-white/10' : '',
      )}
    >
      {label}
    </button>
  );
}
